import {Injectable, Injector} from '@angular/core';
import {ConfigurationResponse, StatusService} from '@middleware';
import {BehaviorSubject, firstValueFrom, Observable} from 'rxjs';
import {filter, shareReplay, take} from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class EnvConfigService {
  // Lazy load StatusService
  private statusService: StatusService | null = null;

  // Use a BehaviorSubject to hold the config, allowing subscribers to get the last value
  private readonly _envConfig$: BehaviorSubject<ConfigurationResponse | null> =
    new BehaviorSubject<ConfigurationResponse | null>(null);

  // Expose an Observable that consumers can subscribe to.
  // shareReplay(1) ensures that if multiple components subscribe,
  // the call is made only once, and new subscribers get the last emitted value.
  public readonly envConfig$: Observable<ConfigurationResponse> = this._envConfig$.pipe(
    filter((config): config is ConfigurationResponse => config !== null),
    shareReplay(1), // Share the source and replay the last value to new subscribers
  );

  constructor(private readonly injector: Injector) {}

  private loadEnvConfigInternal(): void {
    // Renamed to avoid confusion with `getEnvConfig`'s promise
    // Lazy load StatusService
    if (!this.statusService) {
      this.statusService = this.injector.get(StatusService);
    }

    this.statusService.getConfigurations().subscribe({
      next: config => {
        this._envConfig$.next(config);
      },
      error: err => {
        console.error('Failed to load environment configuration:', err);
        this._envConfig$.error(err);
      },
    });
  }

  public async getEnvConfig(): Promise<ConfigurationResponse> {
    // If config hasn't been loaded yet, trigger the load
    try {
      if (this._envConfig$.getValue() === null) {
        this.loadEnvConfigInternal();
      }
      return await firstValueFrom(this.envConfig$.pipe(take(1)));
    } catch (error) {
      console.log('Failed to get env config: ', error);
      // Fallback logic
      return {use_header_auth: false} as ConfigurationResponse;
    }
  }
}
